// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: pictures and files added to an agent prompt, shared by the thread composer and the new
// chat page: up to 20, files up to 50 MB and pictures up to 10 MB each and 80 MB together (T3
// Code's limits; bigger pictures are shrunk to fit), picture previews as object URLs (let go when removed or when the
// composer goes away), and each uploaded to Konvo on send for the machine to fetch. With a draft
// key they are kept with the prompt's draft on this device (AgentDraftFiles) and come back when
// the composer opens. Names are unique within a prompt (a second image.png is image-2.png), as the
// prompt refers to its files by name (AgentAttached).

import {FileAttachmentCard} from '@app/features/channel/components/embeds/attachments/AttachmentFile';
import styles from '@app/features/konvo_agents/components/AgentThread.module.css';
import {loadDraftFiles, saveDraftFiles} from '@app/features/konvo_agents/state/AgentDraftFiles';
import AgentDrafts from '@app/features/konvo_agents/state/AgentDrafts';
import {uploadAgentFiles} from '@app/features/konvo_agents/state/AgentUploads';
import {uniqueFileName} from '@app/features/konvo_agents/utils/AgentAttached';
import {AttachmentPreviewModal} from '@app/features/messaging/components/AttachmentPreviewModal';
import {
	isPastedTextName,
	nextPastedTextName,
	pastedTextFile,
	pastedTextNames,
} from '@app/features/pasted_text/PastedText';
import type {PastedTextSource} from '@app/features/pasted_text/PastedTextFold';
import {openMediaViewerForFiles} from '@app/features/ui/commands/MediaViewerCommands';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {
	AGENT_UPLOAD_IMAGE_MAX_BYTES,
	AGENT_UPLOAD_IMAGES_TOTAL_MAX_BYTES,
	AGENT_UPLOAD_MAX_BYTES,
	AGENT_UPLOAD_MAX_FILES,
	type AgentUploadInput,
	isAgentUploadImage,
} from '@konvo/schema/src/domains/konvo/Agents';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconX} from '@tabler/icons-react';
import {useEffect, useRef, useState} from 'react';

const TOO_LARGE_DESCRIPTOR = msg({
	message: '{name} is larger than 50 MB.',
	comment: 'Agents composer: toast when a file added to a prompt is too large. {name} is the file name.',
});
const PICTURE_TOO_LARGE_DESCRIPTOR = msg({
	message: '{name} is larger than 10 MB and could not be made smaller.',
	comment:
		'Agents composer: toast when a picture over the 10 MB limit for pictures could not be shrunk to fit. {name} is the file name.',
});
const TOO_MANY_DESCRIPTOR = msg({
	message: 'Up to {count} pictures or files per message.',
	comment:
		'Agents composer: toast when more pictures or files are added to one prompt than it takes. {count} is the limit, such as 20.',
});
const PICTURES_TOO_LARGE_DESCRIPTOR = msg({
	message: '{name} was left out: pictures can come to 80 MB per message.',
	comment:
		'Agents composer: toast when adding a picture would take the pictures of one prompt over 80 MB together. {name} is the file name.',
});
const OPEN_FILE_DESCRIPTOR = msg({
	message: 'Open {name}',
	comment:
		'Agents composer: accessible label of an added picture; it opens the picture full size. {name} is the file name.',
});
const REMOVE_FILE_DESCRIPTOR = msg({
	message: 'Remove {name}',
	comment:
		'Agents composer: accessible label of the button that removes an added picture or file. {name} is the file name.',
});

export type PendingFile = {key: string; file: File; preview: string | null};

/** A composer's long pastes, for AgentPromptInput: each is one of its files, shown as a fold. */
export type PastedFiles = {
	/** Adds the paste as a file; its name for the fold, or null when it can't be added. */
	add: (text: string) => string | null;
	source: (name: string) => PastedTextSource | null;
	removed: (name: string) => void;
	restored: (name: string) => void;
};

/**
 * A picture over the 10 MB the agent tools take, as a JPEG that fits: full size first, then
 * smaller (the models see pictures at under 2,000 px anyway). Null when it cannot be read.
 */
async function shrinkPicture(file: File): Promise<File | null> {
	let bitmap: ImageBitmap;
	try {
		bitmap = await createImageBitmap(file);
	} catch {
		return null;
	}
	try {
		for (const edge of [8192, 4096, 2048]) {
			const scale = Math.min(1, edge / Math.max(bitmap.width, bitmap.height));
			const canvas = document.createElement('canvas');
			canvas.width = Math.max(1, Math.round(bitmap.width * scale));
			canvas.height = Math.max(1, Math.round(bitmap.height * scale));
			const context = canvas.getContext('2d');
			if (!context) return null;
			// JPEG has no transparency: see-through parts become white, not black.
			context.fillStyle = '#fff';
			context.fillRect(0, 0, canvas.width, canvas.height);
			context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
			// Null when the canvas is too big for the browser (iOS caps its area); the next size may fit.
			const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
			if (blob && blob.size <= AGENT_UPLOAD_IMAGE_MAX_BYTES)
				return new File([blob], `${file.name.replace(/\.[^.]+$/, '') || 'picture'}.jpg`, {type: 'image/jpeg'});
		}
		return null;
	} finally {
		bitmap.close();
	}
}

export function pendingOf(file: File): PendingFile {
	const image = isAgentUploadImage(file.type);
	return {key: `${Date.now()}-${Math.random()}`, file, preview: image ? URL.createObjectURL(file) : null};
}

/** The pictures' bytes among some added files (pictures can come to 80 MB per prompt together). */
export function pictureBytesOf(files: ReadonlyArray<PendingFile>): number {
	return files.reduce((sum, pending) => sum + (isAgentUploadImage(pending.file.type) ? pending.file.size : 0), 0);
}

/**
 * The files that fit a prompt beside those it has already (`taken`): up to 20, big pictures shrunk,
 * with a toast for each left out.
 */
export async function fitAgentFiles(
	i18n: I18n,
	incoming: ReadonlyArray<File>,
	taken: {count: number; pictureBytes: number},
): Promise<Array<File>> {
	if (!incoming.length) return [];
	const room = AGENT_UPLOAD_MAX_FILES - taken.count;
	if (incoming.length > room) ToastCommands.error(i18n._(TOO_MANY_DESCRIPTOR, {count: AGENT_UPLOAD_MAX_FILES}));
	const fitting: Array<File> = [];
	let pictureBytes = taken.pictureBytes;
	for (const file of incoming.slice(0, Math.max(0, room))) {
		let fits: File | null = file;
		if (isAgentUploadImage(file.type) && file.size > AGENT_UPLOAD_IMAGE_MAX_BYTES) {
			fits = await shrinkPicture(file);
			if (!fits) {
				ToastCommands.error(i18n._(PICTURE_TOO_LARGE_DESCRIPTOR, {name: file.name}));
				continue;
			}
		} else if (file.size > AGENT_UPLOAD_MAX_BYTES) {
			ToastCommands.error(i18n._(TOO_LARGE_DESCRIPTOR, {name: file.name}));
			continue;
		}
		if (isAgentUploadImage(fits.type)) {
			if (pictureBytes + fits.size > AGENT_UPLOAD_IMAGES_TOTAL_MAX_BYTES) {
				ToastCommands.error(i18n._(PICTURES_TOO_LARGE_DESCRIPTOR, {name: file.name}));
				continue;
			}
			pictureBytes += fits.size;
		}
		fitting.push(fits);
	}
	return fitting;
}

/**
 * A composer's added files. `reserved`: files the prompt has besides these (the quoted item's, on
 * the new chat page), counted towards the limits.
 */
export function usePendingFiles(draftKey?: string, reserved?: () => {count: number; pictureBytes: number}) {
	const {i18n} = useLingui();
	const [files, setFiles] = useState<Array<PendingFile>>([]);
	const filesRef = useRef(files);
	filesRef.current = files;
	useEffect(
		() => () => {
			for (const pending of filesRef.current) if (pending.preview) URL.revokeObjectURL(pending.preview);
		},
		[],
	);
	// The draft's files come back first; until then nothing is saved over them.
	const restored = useRef<string | null>(null);
	useEffect(() => {
		restored.current = null;
		if (!draftKey) return;
		let live = true;
		void loadDraftFiles(draftKey).then((kept) => {
			if (!live) return;
			restored.current = draftKey;
			if (kept.length) setFiles((current) => [...kept.map(pendingOf), ...current].slice(0, AGENT_UPLOAD_MAX_FILES));
		});
		return () => {
			live = false;
		};
	}, [draftKey]);
	useEffect(() => {
		if (!draftKey || restored.current !== draftKey) return;
		void saveDraftFiles(
			draftKey,
			files.map((pending) => pending.file),
		);
		AgentDrafts.setFileCount(draftKey, files.length);
	}, [draftKey, files]);
	// Long pastes whose fold was taken out, kept for undo (their names stay taken).
	const putAside = useRef(new Map<string, File>());
	const add = async (incoming: ReadonlyArray<File>) => {
		const other = reserved?.() ?? {count: 0, pictureBytes: 0};
		const fitting = await fitAgentFiles(i18n, incoming, {
			count: filesRef.current.length + other.count,
			pictureBytes: pictureBytesOf(filesRef.current) + other.pictureBytes,
		});
		const taken = new Set([...filesRef.current.map((pending) => pending.file.name), ...putAside.current.keys()]);
		const added = fitting.map((file) => {
			const name = uniqueFileName(file.name || 'file', taken);
			taken.add(name);
			return pendingOf(
				name === file.name ? file : new File([file], name, {type: file.type, lastModified: file.lastModified}),
			);
		});
		if (!added.length) return added;
		// Now, not on the next render: a paste straight after sees these names as taken.
		filesRef.current = [...filesRef.current, ...added].slice(0, AGENT_UPLOAD_MAX_FILES);
		setFiles(filesRef.current);
		return added;
	};
	const remove = (key: string) => {
		const gone = files.find((pending) => pending.key === key);
		if (gone?.preview) URL.revokeObjectURL(gone.preview);
		setFiles(files.filter((pending) => pending.key !== key));
	};
	// Long pastes: each is a text file shown as a fold in the prompt. A fold taken out drops its file,
	// kept here so undo can bring it back.
	const pasted: PastedFiles = {
		add: (text) => {
			if (filesRef.current.length + (reserved?.().count ?? 0) >= AGENT_UPLOAD_MAX_FILES) {
				ToastCommands.error(i18n._(TOO_MANY_DESCRIPTOR, {count: AGENT_UPLOAD_MAX_FILES}));
				return null;
			}
			const file = pastedTextFile(
				text,
				nextPastedTextName([...filesRef.current.map((pending) => pending.file.name), ...putAside.current.keys()]),
			);
			if (file.size > AGENT_UPLOAD_MAX_BYTES) {
				ToastCommands.error(i18n._(TOO_LARGE_DESCRIPTOR, {name: file.name}));
				return null;
			}
			filesRef.current = [...filesRef.current, pendingOf(file)];
			setFiles(filesRef.current);
			return file.name;
		},
		source: (name) => {
			const file = filesRef.current.find((pending) => pending.file.name === name)?.file;
			return file ? {key: file, name, size: file.size, loadBlob: async () => file} : null;
		},
		removed: (name) => {
			const gone = filesRef.current.find((pending) => pending.file.name === name);
			if (!gone || !isPastedTextName(name)) return;
			putAside.current.set(name, gone.file);
			filesRef.current = filesRef.current.filter((pending) => pending !== gone);
			setFiles(filesRef.current);
		},
		restored: (name) => {
			const file = putAside.current.get(name);
			if (!file || filesRef.current.some((pending) => pending.file.name === name)) return;
			putAside.current.delete(name);
			filesRef.current = [...filesRef.current, pendingOf(file)];
			setFiles(filesRef.current);
		},
	};
	// After sending: the draft's files are forgotten now, not on the next render, because the
	// composer often goes away straight after (the new chat page opens the thread it started).
	const clear = (submitted?: ReadonlyArray<File>) => {
		const remove = new Set(submitted ?? filesRef.current.map((pending) => pending.file));
		for (const pending of filesRef.current)
			if (remove.has(pending.file) && pending.preview) URL.revokeObjectURL(pending.preview);
		filesRef.current = filesRef.current.filter((pending) => !remove.has(pending.file));
		setFiles(filesRef.current);
		if (!filesRef.current.length) putAside.current.clear();
		if (draftKey) {
			void saveDraftFiles(
				draftKey,
				filesRef.current.map((pending) => pending.file),
			);
			AgentDrafts.setFileCount(draftKey, filesRef.current.length);
		}
	};

	// Each file goes to Konvo first; the prompt then names them and the machine fetches them.
	const uploads = (): Promise<Array<AgentUploadInput>> => uploadAgentFiles(files.map((pending) => pending.file));
	const full = files.length + (reserved?.().count ?? 0) >= AGENT_UPLOAD_MAX_FILES;
	return {files, add, remove, clear, uploads, pasted, full};
}

export type PendingFilesState = ReturnType<typeof usePendingFiles>;

/**
 * The added pictures (thumbnails that open full size, like WhatsApp's) and files (the chat's file
 * card, opening the attachment preview), each with a remove button.
 */
export function PendingFiles({
	files: all,
	prompt,
	onRemove,
	dataFlx,
}: {
	files: ReadonlyArray<PendingFile>;
	/** The prompt: pasted text with a fold in it shows there, not here. */
	prompt?: string;
	/** Left out where the files can no longer be taken off (a prompt on its way). */
	onRemove?: (key: string) => void;
	dataFlx?: string;
}) {
	const {i18n} = useLingui();
	const folded = prompt ? pastedTextNames(prompt) : null;
	const shown = folded?.size ? all.filter((pending) => !folded.has(pending.file.name)) : all;
	if (shown.length === 0) return null;
	// Pictures first, as a row of thumbnails, then the file cards (each a row of its own on a phone).
	const files = [...shown.filter((pending) => pending.preview), ...shown.filter((pending) => !pending.preview)];
	// One viewer for all the added pictures, opened on the one tapped.
	const pictures = files.flatMap((pending) =>
		pending.preview ? [{key: pending.key, src: pending.preview, file: pending.file}] : [],
	);
	const open = (key: string) =>
		void openMediaViewerForFiles(
			pictures.map(({src, file}) => ({
				src,
				filename: file.name,
				kind: 'image' as const,
				contentType: file.type,
				fileSize: file.size,
			})),
			pictures.findIndex((picture) => picture.key === key),
		);
	const preview = (file: File) =>
		ModalCommands.push(
			modal(() => (
				<AttachmentPreviewModal
					attachment={{name: file.name, content_type: file.type, size: file.size}}
					loadBlob={async () => file}
					onClose={() => ModalCommands.pop()}
				/>
			)),
		);
	return (
		<div className={styles.pending} data-flx={dataFlx}>
			{files.map((pending) =>
				pending.preview ? (
					<div key={pending.key} className={styles.pendingItem}>
						<button
							type="button"
							className={styles.pendingOpen}
							onClick={() => open(pending.key)}
							aria-label={i18n._(OPEN_FILE_DESCRIPTOR, {name: pending.file.name})}
							data-flx={dataFlx ? `${dataFlx}.open` : undefined}
						>
							<img src={pending.preview} alt="" className={styles.pendingImage} />
						</button>
						{onRemove && (
							<button
								type="button"
								className={styles.pendingRemove}
								onClick={() => onRemove(pending.key)}
								aria-label={i18n._(REMOVE_FILE_DESCRIPTOR, {name: pending.file.name})}
								title={i18n._(REMOVE_FILE_DESCRIPTOR, {name: pending.file.name})}
							>
								<IconX size={12} stroke={2.2} />
							</button>
						)}
					</div>
				) : (
					// Other files: the chat's file card, opening the same preview a sent file opens.
					<div key={pending.key} className={styles.pendingCard} data-flx={dataFlx ? `${dataFlx}.file` : undefined}>
						<FileAttachmentCard
							fileName={pending.file.name}
							contentType={pending.file.type}
							size={pending.file.size}
							onPreview={() => preview(pending.file)}
							onRemove={onRemove ? () => onRemove(pending.key) : undefined}
							removeLabel={i18n._(REMOVE_FILE_DESCRIPTOR, {name: pending.file.name})}
						/>
					</div>
				),
			)}
		</div>
	);
}
